import type { CompanyInfo } from '../types-company'

/**
 * VERIFIED company information (greenbaybio.com + product sheets).
 * Do not add unverified facts (founding year, employee/customer counts, awards, guaranteed results).
 * Fields with no verified value are left empty and the UI hides them.
 */
export const company: CompanyInfo = {
  name: 'GreenbayBio',
  legalName: 'Greenbay Biotech International Pvt Ltd.',
  shortName: 'Greenbay',
  tagline: 'Sustainable Bio-Innovation for Indian Farmers',
  description:
    'Greenbay Biotech International develops bio-fertilizers, biostimulants and plant-strength solutions that rebuild soil biology and help Indian farmers grow stronger, more resilient crops — the natural way.',
  mission:
    'To advance sustainable agriculture and enrich lives across the country for generations to come, by delivering innovative, reliable products that meet the real, unmet needs of farmers.',
  vision:
    'To help achieve food security, end hunger, improve nutrition and promote sustainable agriculture through bio-innovation.',
  philosophy: {
    quote:
      'The best crop protection comes from the plant’s own resistance — which is why healthy soil biology is essential for healthy crops.',
    author: 'Sir Pius Floris, Plant Health Cure B.V., Netherlands',
  },
  email: 'support@greenbaybio.com',
  phoneDisplay: '+91 33 4064 6022',
  phoneHref: '+913340646022',
  // No dedicated WhatsApp business number was published. Mapped to the main line for now —
  // replace with the official WhatsApp Business number during backend integration.
  whatsappNumber: '913340646022',
  whatsappNote: 'Chat is routed to our main office line. A dedicated WhatsApp number will be added soon.',
  address: {
    line1: '2B, Grant Lane, 3rd Floor',
    line2: 'Room No. 304',
    city: 'Kolkata',
    postcode: '700012',
    country: 'India',
    full: '2B, Grant Lane, 3rd Floor, Room No. 304, Kolkata 700012, India',
    mapQuery: '2B Grant Lane, Kolkata 700012, India',
  },
  businessHours: '', // not published — UI hides when empty
  socials: [], // none published — UI hides when empty
  values: [
    {
      title: 'Soil biology first',
      body: 'We work on the soil, not just the plant. Living, balanced soil is where healthy crops begin — so our products rebuild the rhizosphere farmers depend on.',
      icon: 'Sprout',
    },
    {
      title: 'Bio-innovation, not shortcuts',
      body: 'Every product is built from natural, bio-based science — microbes, botanical extracts, humic substances and amino acids — designed to reduce dependence on harsh chemistry.',
      icon: 'FlaskConical',
    },
    {
      title: 'Made for Indian farms',
      body: 'Dosage programmes are written crop-by-crop and stage-by-stage for real Indian growing conditions, from grape and pomegranate to sugarcane, cotton and paddy.',
      icon: 'MapPinned',
    },
    {
      title: 'Honest by design',
      body: 'We publish compositions, specifications, licences and precautions in full — and we never promise guaranteed yields we cannot stand behind.',
      icon: 'ShieldCheck',
    },
  ],
  partners: [
    { name: 'Plant Health Cure B.V.', note: 'Soil-biology pioneers, Netherlands' },
    { name: 'Humic Growth', note: 'Humic & fulvic technology' },
    { name: 'Crop BioLife', note: 'Botanical foliar science, South Africa' },
    { name: 'Hydretain', note: 'Root-zone moisture management' },
    { name: 'Arable', note: 'Field & weather intelligence' },
  ],
  certifications: [
    'NPOP — approved for use in organic farming (India), across the eligible range',
    'FCO — registered under the Fertilizer Control Order (India)',
    'USDA BioPreferred® — Hydretain, verified bio-based content',
    'EU Reg. 834/2007 & NOP NF U 44-051 — Biovin organic biostimulant',
  ],
  services: [
    {
      title: 'Terralife',
      body: 'Our soil-life restoration approach — matching biology, biostimulants and nutrition to bring depleted soil back into balance.',
      icon: 'Layers',
    },
    {
      title: 'Soil Biology Analysis',
      body: 'Assessment of the living soil so product choice and dosage can be tailored to what a field actually needs.',
      icon: 'Microscope',
    },
  ],
  milestones: [
    {
      label: 'Approach',
      title: 'Rooted in soil biology',
      body: 'GreenbayBio starts where crops start — in the rhizosphere. Our range is built to repopulate, feed and protect the living soil.',
    },
    {
      label: 'Science',
      title: 'Global pioneers, Indian fields',
      body: 'We bring together soil-biology, humic, botanical and moisture-management science and adapt it to Indian crops and conditions.',
    },
    {
      label: 'Range',
      title: 'A complete bio-input system',
      body: 'From bio-fertilizers and soil improvers to biostimulants, foliar strength and water management — inputs designed to work together.',
    },
    {
      label: 'Reach',
      title: 'Delivered through retailers',
      body: 'Products reach farmers through a trusted retailer and distributor network, backed by crop-specific agronomic guidance.',
    },
  ],  apps: {
    playStore: 'https://play.google.com/store/apps/details?id=com.fibermax.greenbay',
    appStore: '', // no iOS listing yet — badge shows "Coming soon" until this is filled
  },
}

/** WhatsApp deep-link with a pre-filled message. */
export function whatsappLink(message: string): string {
  return `https://wa.me/${company.whatsappNumber}?text=${encodeURIComponent(message)}`
}

/** Google Maps embed / directions helpers. */
export const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(company.address.mapQuery)}&output=embed`
export const mapLinkUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.address.mapQuery)}`
